import React, {useEffect, useState} from 'react';
import {Navigate, useLocation} from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Tooltip,
  Typography
} from '@mui/material';
import {ArrowBack, CheckCircleOutline, Download, Email, RestartAlt, Schedule} from '@mui/icons-material';
import ArticleIcon from '@mui/icons-material/Article';
import {t} from 'ttag';
import {DateTime} from 'luxon';
import useFadeNavigate from './useFadeNavigate';
import {
  useGenerateTicketUsageDeclarationMutation,
  useGetScheduledTicketUsageDeclarationsMutation,
  useScheduleTicketUsageDeclarationMutation
} from '../../store/apis/ccs';
import type {ScheduledDeclarationInfo} from '../../store/apis/ccs/declarationsEndpoints';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type ActionStatus = 'idle' | 'loading' | 'success' | 'error';

function isTicketResolved(ticket: any): boolean {
  const coupons: any[] = ticket?.documentInfo?.coupons ?? [];
  if (coupons.length === 0) return false;
  return coupons.every((coupon) => String(coupon.couponStatus ?? '').toUpperCase() !== 'O');
}

const TicketUsageDeliveryPage = () => {
  const navigate = useFadeNavigate();
  const location = useLocation();
  const completedTickets: any[] = (location.state as any)?.completedTickets ?? [];

  const [generateDeclaration] = useGenerateTicketUsageDeclarationMutation();
  const [scheduleDeclaration] = useScheduleTicketUsageDeclarationMutation();
  const [getScheduledDeclarations] = useGetScheduledTicketUsageDeclarationsMutation();

  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [emailInputError, setEmailInputError] = useState<string | null>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [actionStatus, setActionStatus] = useState<ActionStatus>('idle');
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingMethod, setPendingMethod] = useState<'email' | 'download' | 'schedule' | null>(null);
  const [scheduledDates, setScheduledDates] = useState<string[]>([]);
  const [wasRescheduled, setWasRescheduled] = useState(false);
  const [existingSchedules, setExistingSchedules] = useState<Record<string, ScheduledDeclarationInfo[]>>({});
  const [isCheckingSchedules, setIsCheckingSchedules] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsCheckingSchedules(true);

    const lookups = completedTickets.map((tk) => {
      const key = `${tk.company}-${tk.number}`;
      return getScheduledDeclarations({company: tk.company, number: tk.number}).then((result: any) => {
        if (result?.data) {
          setExistingSchedules((prev) => ({...prev, [key]: result.data}));
        }
      });
    });

    Promise.all(lookups).finally(() => {
      if (!cancelled) setIsCheckingSchedules(false);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (completedTickets.length === 0) {
    return <Navigate to='/declarations/ticket-usage' replace />;
  }

  const ticketCount = completedTickets.length;
  const allResolved = completedTickets.every(isTicketResolved);

  const activeSchedules = completedTickets
    .map((tk) => {
      const key = `${tk.company}-${tk.number}`;
      const active = (existingSchedules[key] ?? []).find((s) => s.status === 'pending' || s.status === 'processing');
      return active ? {ticket: tk, schedule: active} : null;
    })
    .filter((entry): entry is {ticket: any; schedule: ScheduledDeclarationInfo} => entry !== null);
  const hasActiveSchedule = activeSchedules.length > 0;

  const commitEmailInput = (inputOverride?: string): boolean => {
    const raw = (inputOverride ?? emailInput).trim().replace(/,\s*$/, '');
    if (!raw) return true;
    if (!EMAIL_REGEX.test(raw)) {
      setEmailInputError(t`Invalid email address`);
      return false;
    }
    if (emails.includes(raw)) {
      setEmailInputError(t`Email already added`);
      return false;
    }
    setEmails((prev) => [...prev, raw]);
    setEmailInput('');
    setEmailInputError(null);
    return true;
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitEmailInput();
    } else if (e.key === 'Backspace' && emailInput === '' && emails.length > 0) {
      setEmails((prev) => prev.slice(0, -1));
    }
  };

  const removeEmail = (index: number) => {
    setEmails((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (method: 'email' | 'download' | 'schedule') => {
    if (method === 'email' || method === 'schedule') {
      const committed = commitEmailInput();
      if (!committed) return;
      if (emails.length === 0) {
        setShowValidation(true);
        return;
      }
    }
    setPendingMethod(method);
    setActionStatus('loading');
    setActionError(null);

    if (method === 'schedule') {
      setWasRescheduled(hasActiveSchedule);
      const result = await scheduleDeclaration({tickets: completedTickets, emails});

      if ('error' in result) {
        setActionStatus('error');
        setActionError((result.error as any)?.error ?? null);
        return;
      }

      setScheduledDates(result.data.map((r) => DateTime.fromISO(r.scheduledFor).toFormat('dd/MM/yyyy')));
      setExistingSchedules((prev) => {
        const next = {...prev};
        result.data.forEach((r, i) => {
          const tk = completedTickets[i];
          if (!tk) return;
          const key = `${tk.company}-${tk.number}`;
          next[key] = [
            {
              ref: r.ref,
              status: 'pending',
              emails,
              scheduledFor: r.scheduledFor,
              lastCheckedAt: null,
              completedAt: null,
            },
            ...(next[key] ?? []),
          ];
        });
        return next;
      });
      setActionStatus('success');
      return;
    }

    const result = await generateDeclaration({
      tickets: completedTickets,
      deliveryMethod: method,
      emails: method === 'email' ? emails : [],
    });

    if ('error' in result) {
      setActionStatus('error');
      setActionError((result.error as any)?.error ?? null);
      return;
    }

    if (method === 'download') {
      const blob = result.data as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = ticketCount === 1
        ? `ticket-usage-${completedTickets[0].company}${completedTickets[0].number}.pdf`
        : 'ticket-usage-declarations.zip';
      a.click();
      URL.revokeObjectURL(url);
    }

    setActionStatus('success');
  };

  const isLoading = actionStatus === 'loading';
  const isSuccess = actionStatus === 'success';

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
      <Card elevation={3}>
        <CardContent sx={{p: 3}}>
          {/* Title */}
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5}}>
            <ArticleIcon sx={{color: 'text.secondary'}} />
            <Typography variant='h6' fontWeight='bold'>{t`Send Declaration`}</Typography>
          </Box>
          <Typography variant='body2' color='text.secondary' sx={{mb: 2.5}}>
            {ticketCount === 1
              ? t`Enter the recipient email to send the declaration (1 ticket).`
              : `${t`Enter the recipient email to send the declarations`} (${ticketCount} ${t`tickets`}).`}
          </Typography>

          {/* Email input */}
          <Box>
            <Typography variant='body2' fontWeight='medium' sx={{mb: 0.75}}>
              {t`Recipient Email(s)`} *
            </Typography>
            <Box
              sx={{
                display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center',
                px: 1.25, py: 0.75, minHeight: 42,
                border: '1px solid',
                borderColor: (showValidation && emails.length === 0) || emailInputError
                  ? 'error.main'
                  : 'rgba(0,0,0,0.23)',
                borderRadius: 1,
                '&:focus-within': {borderColor: 'primary.main', borderWidth: '2px'},
                cursor: 'text',
              }}
              onClick={() => (document.getElementById('email-chip-input') as HTMLInputElement | null)?.focus()}
            >
              {emails.map((email, i) => (
                <Chip key={i} label={email} size='small' onDelete={() => removeEmail(i)} sx={{height: 26}} />
              ))}
              <Box
                component='input'
                id='email-chip-input'
                value={emailInput}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setEmailInput(e.target.value);
                  setEmailInputError(null);
                }}
                onKeyDown={handleEmailKeyDown}
                onBlur={() => commitEmailInput()}
                placeholder={emails.length === 0 ? 'email@example.com' : ''}
                sx={{
                  border: 'none', outline: 'none', flex: 1, minWidth: 180,
                  fontSize: '0.875rem', fontFamily: 'inherit',
                  bgcolor: 'transparent', color: 'text.primary', py: 0.25,
                }}
              />
            </Box>
            {emailInputError && (
              <Typography variant='caption' color='error' sx={{mt: 0.5, display: 'block'}}>
                {emailInputError}
              </Typography>
            )}
            {showValidation && emails.length === 0 && !emailInputError && (
              <Typography variant='caption' color='error' sx={{mt: 0.5, display: 'block'}}>
                {t`At least one email address is required.`}
              </Typography>
            )}
            <Typography variant='caption' color='text.secondary' sx={{mt: 0.5, display: 'block'}}>
              {t`Press Enter or comma to add each email.`}
            </Typography>
          </Box>

          {hasActiveSchedule && (
            <Alert severity='warning' icon={<Schedule fontSize='inherit' />} sx={{mt: 2.5}}>
              <Typography variant='body2' fontWeight='bold' sx={{mb: 0.5}}>
                {ticketCount > 1
                  ? t`One or more tickets already has a scheduled send`
                  : t`This ticket already has a scheduled send`}
              </Typography>
              {activeSchedules.map(({ticket, schedule}, i) => (
                <Typography key={i} variant='body2' sx={{mb: 0.25}}>
                  {ticketCount > 1 && <strong>{`${ticket.company}${ticket.number}: `}</strong>}
                  {t`will be sent to`} <strong>{schedule.emails.join(', ')}</strong> {t`on`}{' '}
                  <strong>{DateTime.fromISO(schedule.scheduledFor).toFormat('dd/MM/yyyy')}</strong>.
                </Typography>
              ))}
              <Typography variant='body2' sx={{mt: 0.5}}>
                {t`Scheduling again below will replace the send(s) above with the new recipients and date.`}
              </Typography>
            </Alert>
          )}

          {!allResolved && (
            <Alert severity='info' sx={{mt: 2.5}}>
              {t`One or more tickets has a coupon still open (not yet flown, refunded, cancelled, reissued or expired). Download and immediate sending are unavailable — schedule the declaration to be sent automatically once every coupon has been resolved.`}
            </Alert>
          )}

          {/* Success feedback */}
          {isSuccess && (
            <Alert severity='success' icon={<CheckCircleOutline />} sx={{mt: 2.5}}>
              {pendingMethod === 'download' && t`Declaration(s) downloaded successfully.`}
              {pendingMethod === 'email' && t`Declaration(s) sent successfully.`}
              {pendingMethod === 'schedule' && wasRescheduled &&
                `${t`Re-scheduled successfully. It will be sent automatically once every coupon has been resolved, expected around`} ${scheduledDates.join(', ')}.`}
              {pendingMethod === 'schedule' && !wasRescheduled &&
                `${t`Scheduled successfully. It will be sent automatically once every coupon has been resolved, expected around`} ${scheduledDates.join(', ')}.`}
            </Alert>
          )}

          {actionStatus === 'error' && (
            <Alert severity='error' sx={{mt: 2.5}}>
              {actionError ?? t`An error occurred. Please try again.`}
            </Alert>
          )}
        </CardContent>
      </Card>

      <Divider />

      {/* Footer */}
      <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
        <Button
          variant='text'
          startIcon={<ArrowBack />}
          sx={{color: 'text.secondary'}}
          onClick={() => navigate('/declarations/ticket-usage/result', {state: {tickets: completedTickets}})}
        >
          {t`Back`}
        </Button>
        <Box sx={{display: 'flex', gap: 1}}>
          <Button
            variant='outlined'
            color='inherit'
            startIcon={<RestartAlt />}
            sx={{color: 'text.secondary', borderColor: 'divider', '&:hover': {borderColor: 'text.secondary', bgcolor: 'action.hover'}}}
            onClick={() => navigate('/declarations/ticket-usage')}
          >
            {t`New Declaration`}
          </Button>
          <Tooltip title={allResolved ? '' : t`Available once every coupon has been resolved`}>
            <span>
              <Button
                variant='contained'
                color='primary'
                startIcon={isLoading && pendingMethod === 'download' ? <CircularProgress size={16} color='inherit' /> : <Download />}
                disabled={isLoading || !allResolved}
                onClick={() => handleSubmit('download')}
                sx={{fontWeight: 600}}
              >
                {t`Download PDF`}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={allResolved ? '' : t`Available once every coupon has been resolved`}>
            <span>
              <Button
                variant='contained'
                color='primary'
                startIcon={isLoading && pendingMethod === 'email' ? <CircularProgress size={16} color='inherit' /> : <Email />}
                disabled={isLoading || !allResolved}
                onClick={() => handleSubmit('email')}
                sx={{fontWeight: 600}}
              >
                {t`Send Declaration`}
              </Button>
            </span>
          </Tooltip>
          {!allResolved && (
            <Tooltip title={isCheckingSchedules ? t`Checking for existing schedules…` : ''}>
              <span>
                <Button
                  variant='contained'
                  color={hasActiveSchedule ? 'warning' : 'secondary'}
                  startIcon={
                    isCheckingSchedules || (isLoading && pendingMethod === 'schedule')
                      ? <CircularProgress size={16} color='inherit' />
                      : <Schedule />
                  }
                  disabled={isLoading || isCheckingSchedules}
                  onClick={() => handleSubmit('schedule')}
                  sx={{fontWeight: 600}}
                >
                  {isCheckingSchedules
                    ? t`Checking Schedules…`
                    : hasActiveSchedule
                      ? t`Re-Schedule Sending`
                      : t`Schedule Sending`}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TicketUsageDeliveryPage;
